import { useThemeContext } from '../../contexts/ThemeContext';
import { Sun, Moon, Monitor } from 'lucide-react';
import type { Theme } from '../../hooks/useTheme';

interface ThemeToggleProps {
  className?: string;
}

export function ThemeToggle({ className = '' }: ThemeToggleProps) {
  const { theme, setTheme } = useThemeContext();

  const options: { value: Theme; icon: string; label: string }[] = [
    { value: 'light', icon: '☀️', label: 'Light' },
    { value: 'system', icon: '💻', label: 'System' },
    { value: 'dark', icon: '🌙', label: 'Dark' },
  ];

  return (
    <div className={`flex items-center gap-1 p-1 bg-stone-100 dark:bg-stone-800 rounded-lg ${className}`}>
      {options.map((opt) => (
        <button
          key={opt.value}
          onClick={() => setTheme(opt.value)}
          className={`
            px-3 py-1.5 rounded-md text-sm font-medium transition-colors duration-200
            ${theme === opt.value
              ? 'bg-white dark:bg-stone-900 text-stone-900 dark:text-stone-100 shadow-sm'
              : 'text-stone-500 dark:text-stone-400 hover:text-stone-600 dark:hover:text-stone-300'}
          `}
          title={opt.label}
          aria-label={`Switch to ${opt.label} theme`}
        >
          <span className="text-base">{opt.icon}</span>
        </button>
      ))}
    </div>
  );
}

// Lucide icons for theme toggle

// Compact version for settings - no redundant label, elegant icons
export function ThemeToggleCompact({ className = '' }: ThemeToggleProps) {
  const { theme, setTheme } = useThemeContext();

  const options: { value: Theme; icon: React.ReactNode; label: string }[] = [
    { value: 'light', icon: <Sun className="w-5 h-5" strokeWidth={1.5} />, label: 'Light' },
    { value: 'system', icon: <Monitor className="w-5 h-5" strokeWidth={1.5} />, label: 'System' },
    { value: 'dark', icon: <Moon className="w-5 h-5" strokeWidth={1.5} />, label: 'Dark' },
  ];

  return (
    <div className={`grid grid-cols-3 gap-2 ${className}`} role="radiogroup" aria-label="Theme">
      {options.map((opt) => (
        <button
          key={opt.value}
          role="radio"
          aria-checked={theme === opt.value}
          onClick={() => setTheme(opt.value)}
          className={`
            group flex flex-col items-center gap-2 p-4 rounded-xl border transition-colors duration-200
            ${theme === opt.value
              ? 'bg-stone-100 dark:bg-stone-800 border-stone-300 dark:border-stone-600 text-stone-900 dark:text-stone-100'
              : 'bg-transparent border-stone-200/60 dark:border-stone-700/50 text-stone-400 dark:text-stone-500 hover:border-stone-300 dark:hover:border-stone-600 hover:text-stone-600 dark:hover:text-stone-300'}
          `}
        >
          {opt.icon}
          <span className="text-xs font-medium">{opt.label}</span>
        </button>
      ))}
    </div>
  );
}
